import axios from "axios";
import React, {useCallback, useContext, useEffect, useMemo, useRef} from "react";
import {useRequestErrorMessage} from "../../utils/useRequestErrorMessage";
import {LanguageContext} from "../../context/LanguageContext";
import {debounce} from "@mui/material";

export const axiosWithInterceptors = axios.create(); // export this and use it in all your components

export const AxiosInterceptorInitializer = () => {
  const languageContext = useContext(LanguageContext);
  const {showGroupedRequestError} = useRequestErrorMessage();

  const groupedMessages = useRef({});

  const showGroupedMessages = useMemo(() => debounce(() => {
    for (const pathName in groupedMessages.current) {
      showGroupedRequestError(groupedMessages.current[pathName], "firstPerGroup");
    }
    groupedMessages.current = {};
  }, 500), [showGroupedRequestError]);

  const trimApiPrefix = (pathName) => {
    let trimmed = pathName.replace(/^\/+/, "");
    const parts = trimmed.split("/");
    if (parts.length > 2 && parts[0] === 'api') {
      return parts.slice(2).join('/');
    }
    return trimmed;
  };

  const isValidUrl = (url) => {
    try {
      return Boolean(new URL(url));
    } catch {
      return false;
    }
  };

  const createInterceptor = useCallback(() => {
    return axiosWithInterceptors.interceptors.response.use(function (response) {
      return response;
    }, function (error) {
      //ignore intentional cancellations
      if (axios.isCancel(error)) {
        return Promise.reject(error);
      }

      const config = error?.config;
      let rawPathName = config?.url || "";
      if (isValidUrl(rawPathName)) {
        rawPathName = new URL(rawPathName).pathname;
      }
      let pathName = trimApiPrefix(rawPathName);
      const apiErrorMessages = languageContext.dictionary?.apiErrorMessages ?? {};
      let errorMessage = matchApiErrorMessage(apiErrorMessages, pathName, error);

      if (typeof errorMessage === "function") {
        errorMessage = errorMessage({
          queryParams: config?.params,
          pathParams: [],
          body: config?.data
        });
      } else if (typeof errorMessage !== "string") {
        pathName = "UNKNOWN_ERROR";
        errorMessage = config?.hideUnknownErrors ? "" : languageContext.dictionary?.unknownError;
      }

      groupedMessages.current[pathName] = groupedMessages.current[pathName] || [];
      if (errorMessage) {
        groupedMessages.current[pathName].push({errorMessage: errorMessage, error: error});
      }

      showGroupedMessages();

      return Promise.reject(error);
    });
  }, [languageContext.language.id, showGroupedMessages]);

  useEffect(() => {
    const resInterceptor = createInterceptor();
    return () => {
      axiosWithInterceptors.interceptors.response.eject(resInterceptor);
    };
  }, [createInterceptor]);

  return <></>;

  function getMatchedErrorMessage(direct, error) {
    if (!direct || typeof direct === "string" || typeof direct === "function") {
      return direct;
    }

    const status = error.response?.status ?? error.request?.status;
    const method = error?.config?.method?.toLowerCase();

    // 1. Check if it's a direct status match: { 404: "Not Found" }
    if (status !== undefined && status in direct) {
      const message = direct[status];
      if (typeof message === "string" || typeof message === "function") {
        return message;
      }
    }

    // 2. Check if it's a method match: { get: "Error on get" } or { get: { 404: "Not Found on get" } }
    if (method !== undefined && method in direct) {
      const methodData = direct[method];

      if (typeof methodData === "string" || typeof methodData === "function") {
        return methodData;
      }

      if (status !== undefined && methodData && typeof methodData === "object" && status in methodData) {
        return methodData[status];
      }
    }

    return undefined;
  }

  function matchApiErrorMessage(apiErrorMessages, pathName, error) {
    const direct = apiErrorMessages[pathName];

    const matched = getMatchedErrorMessage(direct, error);
    if (direct && matched) {
      return matched;
    }

    for (const key of Object.keys(apiErrorMessages)) {
      if (key.includes("*")) {
        const regexKey = new RegExp(`^${key.split("*").map(s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join("[^/]+")}$`);
        if (regexKey.test(pathName)) {
          return getMatchedErrorMessage(apiErrorMessages[key], error);
        }
      }
    }

    return undefined;
  }
};